<script>
import TwButton from '@/components/generic/tw-button.vue'
import TwInput from '@/components/generic/forms/tw-input.vue'
import selectableRows from '@/mixins/selectableRows.js'
import TwCheckbox from '@/components/generic/forms/tw-checkbox.vue'
import { rowFieldValue } from '@/mixins/lists/rowFieldValueNested.js'

export default {
  name: 'TwTable',
  components: { TwCheckbox, TwInput, TwButton },
  mixins:[selectableRows],
  props: {
    rows: {
      type: Array,
      default: () => []
    },
    fields: {
      type: Array,
      default: () => []
    },
    orderBy: {
      type: String,
      default: null
    },
    orderDirection: {
      type: String,
      default: 'asc'
    },
    loading: {
      type: Boolean,
      default: null
    },
    loadingRowCount: {
      type: Number,
      default: 10, // Number of skeleton rows during loading
    },
    filters: {
      type: Object,
      default: () => ({})
    },
    rowClass: {
      type: Function,
      default: () => (row) => {
        return ''
      }
    }
  },
  emits: ['sort-changed', 'filters-changed'],
  computed: {
    fieldsSearchable() {
      return this.fields.filter((field) => field.searchable);
    }
  },
  methods: {
    rowFieldValue,
    sortChanged(orderBy, orderDirection) {
      this.$emit('sort-changed', { orderBy, orderDirection })
    },
    filterChanged(field, value) {
      const filters = { ...this.filters }
      if(typeof value === 'string' && value.length === 0) {
        delete filters[field]
      }
      else {
        filters[field] = value
      }
      this.$emit('filters-changed', filters)
    }
  }
}
</script>

<template>
  <table class="min-w-full divide-y divide-gray-300">
    <thead class="whitespace-nowrap">
      <tr>
        <th v-if="selectable">
          &nbsp;
        </th>
        <th v-for="(field, index) in fields" :key="'table-column-key-' + index" scope="col" :class="['p-3 text-left text-xs font-medium tracking-wide text-gray-500']">
          <tw-button v-if="field.sortable" variant="link" :class="[{ 'font-extrabold text-gray-900': field.name === orderBy }, 'font-lato text-xs']" @click="sortChanged(field.name, orderDirection === 'asc'? 'desc':'asc')">
            {{ field.label ? field.label: field.name }} <icon :icon="orderBy === field.name ? (orderDirection === 'asc' ? 'sort-down':'sort-up') :'sort'" />
          </tw-button>
          <template v-else>
            {{ field.label ? field.label: field.name }}
          </template>
        </th>
      </tr>
      <tr v-if="fieldsSearchable.length > 0">
        <th v-if="selectable">
          &nbsp;
        </th>
        <th v-for="(field, index) in fields" :key="'table-column-filter-key-' + index" scope="col" class="text-left text-sm font-forms text-gray-400 px-3">
          <tw-input v-if="field.searchable" :ref="'field' + index" class="mb-3" size="sm" :placeholder="'Search in ' + (field.label? field.label:field.name) + '...' " @update:model-value="filterChanged(field.name, $event)" />
        </th>
      </tr>
    </thead>
    <tbody class="divide-y divide-gray-200">
      <template v-if="loading">
        <tr v-for="n in loadingRowCount" :key="n">
          <td v-if="selectable" />
          <td v-for="(field, index) in fields" :key="index" class="p-2 border border-gray-300">
            <!-- Skeleton loader -->
            <div class="animate-pulse bg-gray-200 h-4 rounded" />
          </td>
        </tr>
      </template>
      <template v-else>
        <tr v-for="(row, rowIndex) in rows" :key="'table-row-key-' + rowIndex" :class="['hover:bg-gray-100', rowClass(row),{'bg-gray-200': isRowSelected(row, 'id')}]">
          <td v-if="selectable">
            <tw-checkbox class="m-2" :model-value="isRowSelected(row, 'id')" @change="toggleRowSelection(row, 'id')" />
          </td>
          <td v-for="(field, fieldIndex) in fields" :key="'table-row-field-key-' + '-' + rowIndex + '-' + fieldIndex" class="min-w-20 py-5 px-3 font-forms text-sm text-gray-900 ">
            <slot :name="'cell(' + field.name +')'" :row="row" :field="field" :value="row[field.name]" :index="rowIndex">
              {{ rowFieldValue(row, field.name, field.fallback, field.formatter) }}
            </slot>
          </td>
        </tr>
      </template>
      <tr v-if="!rows.length && !loading">
        <td :colspan="fields.length" class="text-center text-gray-500 pt-10 py-8">
          <slot name="noData">
            No data available.
          </slot>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<style scoped>

</style>
